/**
 * LA ACTIVIDAD: lo que ya se jugó, de a diez y con filtros.
 *
 * QUÉ PROBLEMA RESUELVE. El Muro mostraba todo junto y en orden: para
 * encontrar cómo le fue a alguien el mes pasado había que bajar cuarenta
 * tarjetas. Ahora se pregunta —este club, esta categoría, estas dos personas,
 * entre estas dos fechas— y salen las diez que contestan.
 *
 * LOS FILTROS VAN AL SERVIDOR Y NO SE APLICAN ACÁ, al revés que los chips del
 * cara a cara. Allá los partidos ya habían viajado enteros; acá la lista puede
 * ser de miles y viene de a diez, así que filtrar después de paginar daría
 * páginas de dos tarjetas y páginas vacías.
 *
 * LA GENTE SE PIDE CUANDO SE ABRE EL PANEL Y NO AL ENTRAR. Ofrecer nombres
 * para el filtro son una consulta por club, y la mayoría de quienes entran a
 * ver qué pasó no van a filtrar nada. Mientras el panel está cerrado, esta
 * pantalla es una sola petición.
 *
 * QUIÉN LO VE. Lo decide el servidor: los partidos de mis clubes, y los
 * amistosos de cualquiera —lo eligió Javier: «si es un amistoso fuera del
 * club cualquiera lo puede ver»—. Esta pantalla no vuelve a preguntárselo.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CATEGORY_LEVELS,
  SEXES,
  type ActivityEntry,
  type ActivityQuery,
  type CategoryLevel,
  type Community,
  type MatchComment,
  type Sex,
  MatchScore,
} from '@tennisup/contracts';

import { ActivityCard } from './ActivityCard';
import { Alert } from './Alert';
import { Paginador } from './Paginado';
import { Select } from './Select';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

/** Lo que hay puesto en el panel. `''` es «sin filtrar por esto». */
interface Filtro {
  communityId: string;
  level: CategoryLevel | '';
  sex: Sex | '';
  /** Los ids de quienes tienen que haber jugado. Con dos, los dos. */
  userIds: string[];
  /** `YYYY-MM-DD`, las dos inclusive. */
  from: string;
  to: string;
}

const VACÍO: Filtro = { communityId: '', level: '', sex: '', userIds: [], from: '', to: '' };

/** Alguien a quien se le puede pedir que aparezca. */
interface Persona {
  userId: string;
  displayName: string;
  communityId: string;
}

export function ActivitySection(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [filtro, setFiltro] = useState<Filtro>(VACÍO);
  const [página, setPágina] = useState(1);
  const [entradas, setEntradas] = useState<ActivityEntry[] | null>(null);
  const [páginas, setPáginas] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const respuesta = await api.wallActivity(aConsulta(filtro, página));
      /*
       * SE COMPRUEBA QUE SEA UNA LISTA, AUNQUE EL TIPO LO PROMETA.
       *
       * El tipo es una promesa sobre el contrato, no una garantía sobre lo que
       * llegó por el cable: una versión vieja del servidor que todavía
       * contesta la lista pelada —como contestaba el Muro— deja `entries`
       * valiendo `Array.prototype.entries`, que es UNA FUNCIÓN. Un `?? []` no
       * la ataja, y React trata la función como un actualizador y tumba la
       * pantalla entera con un error que no nombra nada de acá.
       *
       * Una lista vacía se dibuja; cualquier otra cosa tumba la página.
       */
      setEntradas(Array.isArray(respuesta.entries) ? respuesta.entries : []);
      setPáginas(typeof respuesta.pages === 'number' ? Math.max(1, respuesta.pages) : 1);
    } catch (caught) {
      setError(errorMessage(t, caught));
      // Lista vacía y no nula: sin esto la pantalla se queda en «Cargando…»
      // para siempre con el error arriba, y no se entiende cuál de las dos
      // cosas está pasando.
      setEntradas([]);
    }
  }, [filtro, página, t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /**
   * CAMBIAR UN FILTRO VUELVE A LA PRIMERA PÁGINA.
   *
   * Quedarse en la página 3 de OTRA lista es el defecto que hace pensar que
   * el filtro no encontró nada: la respuesta llega con dos tarjetas, o con
   * ninguna, y parece que la persona no jugó nunca.
   */
  function cambiar(cambio: Partial<Filtro>): void {
    setFiltro((antes) => ({ ...antes, ...cambio }));
    setPágina(1);
  }

  /**
   * DESPUÉS DE COMENTAR SE VUELVE A PEDIR LA PÁGINA.
   *
   * Un comentario nuevo trae su id y su permiso de borrado del servidor, y
   * armarlo acá sería una segunda idea de quién puede borrar qué.
   *
   * Las reacciones son la excepción: contestan la cuenta nueva y se aplica
   * sola, porque volver a pedir diez tarjetas por un 🔥 hace que el botón
   * tarde medio segundo en prenderse y nadie lo aprieta dos veces.
   */
  async function comentar(entrada: ActivityEntry, body: string): Promise<void> {
    try {
      if (entrada.kind === 'TOURNAMENT') await api.addComment(entrada.id, body);
      else await api.addChallengeComment(entrada.id, body);
      await cargar();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  /**
   * CORREGIR EL MARCADOR de un reto ya anotado.
   *
   * EL ORDEN DEL MARCADOR NO SE TOCA. `ActivityCard` dibuja `home` = quien
   * retó (`fromUserId`), que es el mismo orden en que el servidor lo guarda;
   * y cuando quien corrige no es `toUserId` —el caso del admin de plataforma
   * y también el de quien retó— el servidor lo guarda tal cual. Invertirlo
   * acá sería invertirlo dos veces.
   *
   * SOLO RETOS. Un partido de torneo se corrige desde el cuadro: `canFixScore`
   * viene `false` en esas tarjetas, así que este camino no se ofrece.
   */
  async function corregirMarcador(
    entrada: ActivityEntry,
    score: MatchScore,
    winnerUserId: string | null,
    superTieBreak: boolean,
  ): Promise<void> {
    try {
      await api.setChallengeResult(entrada.id, { winnerUserId, score, superTieBreak });
      await cargar();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function borrarComentario(comentario: MatchComment): Promise<void> {
    try {
      await api.removeComment(comentario.id);
      await cargar();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function reaccionar(entrada: ActivityEntry, emoji: string, on: boolean): Promise<void> {
    try {
      const cuenta =
        entrada.kind === 'TOURNAMENT'
          ? await api.setReaction(entrada.id, emoji, on)
          : await api.setChallengeReaction(entrada.id, emoji, on);
      setEntradas((lista) =>
        lista === null
          ? null
          : lista.map((x) => (x.id === entrada.id ? { ...x, reactions: cuenta } : x)),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  return (
    <>
      {error !== null && <Alert kind="error">{error}</Alert>}

      <Filtros filtro={filtro} onChange={cambiar} onClear={() => cambiar(VACÍO)} />

      <p className="help wall-order">{t('wall.newestFirst')}</p>

      {entradas === null ? (
        <p className="help">{t('common.loading')}</p>
      ) : entradas.length === 0 ? (
        <p className="help">{t(hayFiltro(filtro) ? 'activity.emptyFiltered' : 'wall.empty')}</p>
      ) : (
        <>
          {entradas.map((entrada) => (
            <ActivityCard
              key={entrada.id}
              entrada={entrada}
              yoId={session?.user.id ?? ''}
              onComment={comentar}
              onDeleteComment={borrarComentario}
              onReact={reaccionar}
              onFixScore={corregirMarcador}
            />
          ))}
          <Paginador
            pagina={página}
            paginas={páginas}
            irA={setPágina}
            de={t('wall.activity')}
          />
        </>
      )}
    </>
  );
}

/**
 * EL PANEL DE FILTROS, CERRADO.
 *
 * Un `<details>` y no seis campos siempre a la vista: lo que la gente viene a
 * hacer acá es mirar, y seis campos arriba empujan la primera tarjeta fuera
 * de la pantalla del teléfono. Abierto queda abierto mientras se juega con
 * él, que es cuando estorba menos.
 *
 * EL RESUMEN VA EN EL TÍTULO. Con el panel cerrado y un filtro puesto, la
 * lista se ve corta sin razón aparente; «Filtros (2)» es lo que explica por
 * qué faltan cosas.
 */
function Filtros({
  filtro,
  onChange,
  onClear,
}: {
  filtro: Filtro;
  onChange: (cambio: Partial<Filtro>) => void;
  onClear: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const [clubes, setClubes] = useState<Community[]>([]);
  const [gente, setGente] = useState<Persona[]>([]);
  const [buscado, setBuscado] = useState('');

  /**
   * LOS CLUBES Y SU GENTE, UNA SOLA VEZ Y AL ABRIR.
   *
   * Se piden los socios de TODOS mis clubes juntos porque el filtro de gente
   * puede usarse sin elegir club: sin eso habría que obligar a elegir uno
   * primero, que es un paso de más para buscar a alguien por el nombre.
   *
   * Si falla, se queda sin nombres y los otros filtros siguen andando: no
   * poder ofrecer una lista de gente no es razón para tapar la pantalla con
   * un error rojo.
   */
  useEffect(() => {
    if (!abierto || clubes.length > 0) return;
    let vivo = true;
    void (async () => {
      try {
        /*
         * SOLO LOS QUE TIENEN MI MEMBRESÍA ACTIVA.
         *
         * `listMyCommunities` trae también los que creé y todavía están
         * esperando aprobación. Ésos no tienen un solo partido que mostrar, y
         * `myMembershipStatus === 'ACTIVE'` es exactamente lo que el servidor
         * usa para decidir qué deja ver: sin este filtro, el desplegable
         * ofrecería un club cuya Actividad viene vacía siempre.
         */
        const míos = (await api.listMyCommunities()).filter(
          (c) => c.myMembershipStatus === 'ACTIVE',
        );
        if (!vivo) return;
        setClubes(míos);
        const socios = await Promise.all(
          míos.map(async (c) =>
            (await api.listMembers(c.id)).map((m) => ({
              userId: m.userId,
              displayName: m.displayName,
              communityId: c.id,
            })),
          ),
        );
        if (vivo) setGente(socios.flat());
      } catch {
        // Sin nombres que ofrecer. Los demás filtros no dependen de esto.
      }
    })();
    return () => {
      vivo = false;
    };
  }, [abierto, clubes.length]);

  /**
   * A QUIÉN SE PUEDE ELEGIR: los del club puesto, o los de todos mis clubes.
   *
   * Una sola vez cada quien, aunque esté en tres clubes: la misma persona en
   * la lista tres veces es tres veces el mismo filtro.
   */
  const elegibles = useMemo(() => {
    const porId = new Map<string, Persona>();
    for (const p of gente) {
      if (filtro.communityId !== '' && p.communityId !== filtro.communityId) continue;
      if (!porId.has(p.userId)) porId.set(p.userId, p);
    }
    return [...porId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [gente, filtro.communityId]);

  const texto = normalizar(buscado);
  const encontrados =
    texto === ''
      ? []
      : elegibles
          .filter(
            (p) => !filtro.userIds.includes(p.userId) && normalizar(p.displayName).includes(texto),
          )
          .slice(0, 8);

  const puestos = filtro.userIds.map(
    (id) => gente.find((p) => p.userId === id) ?? { userId: id, displayName: id, communityId: '' },
  );

  const cuántos = cuántosFiltros(filtro);

  return (
    <details
      className="act-filters"
      open={abierto}
      onToggle={(event) => setAbierto((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>
        {t('activity.filters')}
        {cuántos > 0 && <span className="act-filter-count">{cuántos}</span>}
      </summary>

      <div className="act-filter-grid">
        <p className="field">
          <label htmlFor="act-club">{t('activity.club')}</label>
          <Select
            id="act-club"
            value={filtro.communityId}
            options={[
              { value: '', label: t('activity.anyClub') },
              ...clubes.map((c) => ({ value: c.id, label: c.name })),
            ]}
            // Cambiar de club puede dejar elegida a gente que no es de ahí, y
            // eso daría una lista vacía sin que se vea por qué. Se limpian.
            onChange={(value) => onChange({ communityId: value, userIds: [] })}
          />
        </p>

        <p className="field">
          <label htmlFor="act-level">{t('activity.level')}</label>
          <Select
            id="act-level"
            value={filtro.level}
            options={[
              { value: '' as const, label: t('activity.anyLevel') },
              ...CATEGORY_LEVELS.map((l) => ({ value: l, label: t(`categoryLevel.${l}`) })),
            ]}
            onChange={(value) => onChange({ level: value })}
          />
        </p>

        <p className="field">
          <label htmlFor="act-sex">{t('activity.sex')}</label>
          <Select
            id="act-sex"
            value={filtro.sex}
            options={[
              { value: '' as const, label: t('activity.anySex') },
              ...SEXES.map((s) => ({ value: s, label: t(`sex.${s}`) })),
            ]}
            onChange={(value) => onChange({ sex: value })}
          />
        </p>

        <p className="field">
          <label htmlFor="act-from">{t('activity.from')}</label>
          <input
            id="act-from"
            type="date"
            value={filtro.from}
            onChange={(event) => onChange({ from: event.target.value })}
          />
        </p>

        <p className="field">
          <label htmlFor="act-to">{t('activity.to')}</label>
          <input
            id="act-to"
            type="date"
            value={filtro.to}
            onChange={(event) => onChange({ to: event.target.value })}
          />
        </p>

        {/*
          LA GENTE: se escribe y se elige, no se despliega.
          Un club de ochenta socios en un desplegable es una lista que hay que
          recorrer con el dedo; tres letras y salen los que importan.
        */}
        <div className="field act-people">
          <label htmlFor="act-people">{t('activity.players')}</label>
          <input
            id="act-people"
            type="search"
            autoComplete="off"
            placeholder={t('activity.playersPlaceholder')}
            value={buscado}
            onChange={(event) => setBuscado(event.target.value)}
          />

          {texto !== '' && encontrados.length === 0 && (
            <p className="help">{t('activity.noPlayer')}</p>
          )}

          {encontrados.length > 0 && (
            <ul className="act-hits">
              {encontrados.map((p) => (
                <li key={p.userId}>
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      onChange({ userIds: [...filtro.userIds, p.userId] });
                      setBuscado('');
                    }}
                  >
                    {p.displayName}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {puestos.length > 0 && (
            <p className="act-chosen">
              {puestos.map((p) => (
                <button
                  key={p.userId}
                  type="button"
                  className="tag act-chip"
                  aria-label={t('activity.removePlayer', { name: p.displayName })}
                  onClick={() =>
                    onChange({ userIds: filtro.userIds.filter((id) => id !== p.userId) })
                  }
                >
                  {p.displayName} <span aria-hidden="true">✕</span>
                </button>
              ))}
            </p>
          )}

          {filtro.userIds.length > 1 && <p className="help">{t('activity.bothPlayed')}</p>}
        </div>
      </div>

      {cuántos > 0 && (
        <p className="act-filter-actions">
          <button
            type="button"
            className="link"
            onClick={() => {
              onClear();
              setBuscado('');
            }}
          >
            {t('activity.clear')}
          </button>
        </p>
      )}
    </details>
  );
}

/**
 * DEL PANEL A LA CONSULTA.
 *
 * LO VACÍO NO VIAJA. `?level=` es un filtro puesto en nada, que no es lo
 * mismo que sin filtro, y con `exactOptionalPropertyTypes` tampoco se puede
 * mandar `undefined` como si no se hubiera puesto la propiedad.
 */
function aConsulta(filtro: Filtro, página: number): ActivityQuery {
  const query: ActivityQuery = {};
  if (filtro.communityId !== '') query.communityId = filtro.communityId;
  if (filtro.level !== '') query.level = filtro.level;
  if (filtro.sex !== '') query.sex = filtro.sex;
  if (filtro.userIds.length > 0) query.userIds = filtro.userIds;
  if (filtro.from !== '') query.from = filtro.from;
  if (filtro.to !== '') query.to = filtro.to;
  if (página > 1) query.page = página;
  return query;
}

function cuántosFiltros(filtro: Filtro): number {
  return (
    (filtro.communityId === '' ? 0 : 1) +
    (filtro.level === '' ? 0 : 1) +
    (filtro.sex === '' ? 0 : 1) +
    (filtro.from === '' ? 0 : 1) +
    (filtro.to === '' ? 0 : 1) +
    filtro.userIds.length
  );
}

function hayFiltro(filtro: Filtro): boolean {
  return cuántosFiltros(filtro) > 0;
}

/** Minúsculas y sin tildes: «Muñóz» y «munoz» son la misma persona. */
function normalizar(texto: string): string {
  return texto
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
